<template>
  <section class="main-contents-wrapper">
    <h2 class="page-title">베팅 포인트 적립 설정</h2>
    <p class="intro">회원의 카지노·슬롯 베팅금에 설정된 적립률을 적용해 직속상부에게 포인트를 지급합니다. 기존 롤링과 별도로 적립되며 공베팅·타이도 포함됩니다. 직속상부가 없으면 지급하지 않습니다.</p>
    <p class="intro">적립 포인트 = 베팅금 × 적립률 ÷ 100 (소수점 버림). 적립률은 0.01~5.00%이며 소수점 둘째 자리까지 입력할 수 있습니다.</p>
    <div class="tab-wrap">
      <button type="button" class="tab-item" :class="{ active: mode === 'site' }" :aria-pressed="mode === 'site'" @click="mode = 'site'">사이트 공통 설정</button>
      <button type="button" class="tab-item" :class="{ active: mode === 'member' }" :aria-pressed="mode === 'member'" @click="mode = 'member'">회원별 설정</button>
      <button type="button" class="tab-item" :class="{ active: mode === 'list' }" :aria-pressed="mode === 'list'" @click="openList">개별 설정 회원 목록</button>
    </div>
    <bet-point-settings v-if="mode === 'site'" />
    <template v-else-if="mode === 'member'">
      <form class="search" @submit.prevent="findMember">
        <label>회원 ID <input v-model="memberSearch" type="text" autocomplete="off" @input="clearMember" /></label>
        <button type="submit">조회</button>
      </form>
      <p v-if="searchError" class="error" role="alert">{{ searchError }}</p>
      <template v-if="memberId">
        <p class="intro">베팅 회원: <strong>{{ memberId }}</strong></p>
        <bet-point-settings :key="memberId + ':' + lookupVersion" :member-id="memberId" />
      </template>
      <p v-else-if="!searchError" class="intro">베팅 회원의 ID를 조회하면 직속상부에게 적용되는 적립 설정을 확인할 수 있습니다.</p>
    </template>
    <section v-else class="list" :aria-busy="loading">
      <h3>개별 설정 회원 목록</h3>
      <p class="intro">회원 ID는 정확히 일치하는 값으로 검색합니다.</p>
      <form class="search" @submit.prevent="searchList">
        <label>구분 <select v-model="filters.category"><option value="">전체</option><option value="casino">카지노</option><option value="slot">슬롯</option></select></label>
        <label>회원 ID <input v-model="filters.memId" type="text" autocomplete="off" placeholder="정확히 일치하는 ID" /></label>
        <button type="submit" :disabled="loading">조회</button>
      </form>
      <p v-if="loading" role="status">회원 목록을 불러오는 중입니다.</p>
      <p v-else-if="listError" class="error" role="alert">{{ listError }} <button type="button" @click="loadList(page)">다시 불러오기</button></p>
      <template v-else>
        <p class="intro">{{ page }} 페이지<template v-if="totalCount !== null"> · 전체 {{ totalCount }}명</template></p>
        <div class="table-scroll"><table class="mainTable">
          <thead><tr><th scope="col">회원 ID</th><th scope="col">설정</th></tr></thead>
          <tbody><tr v-for="(row, index) in members" :key="row.memId + ':' + index"><td>{{ row.memId }}</td><td><button type="button" @click="openMember(row.memId)">확인·수정</button></td></tr><tr v-if="!members.length"><td colspan="2">개별 설정 회원이 없습니다.</td></tr></tbody>
        </table></div>
        <div class="pages"><button type="button" :disabled="page <= 1" @click="loadList(page - 1)">이전</button><span>{{ page }}</span><button type="button" :disabled="!hasNext" @click="loadList(page + 1)">다음</button></div>
      </template>
    </section>
  </section>
</template>
<script>
import BetPointSettings from '@/components/common/BetPointSettings'
import { getMemberBetPointList } from '@/api/setting'

export default {
  name: 'BetPointSetting',
  components: { BetPointSettings },
  data () { return { mode: 'site', memberSearch: '', memberId: '', lookupVersion: 0, searchError: '', filters: { category: '', memId: '' }, query: {}, members: [], page: 1, totalCount: null, hasNext: false, loading: false, listError: '' } },
  methods: {
    clearMember () { this.memberId = ''; this.searchError = '' },
    findMember () {
      this.clearMember()
      const id = this.memberSearch.trim()
      if (!id) { this.searchError = '회원 ID를 입력해주세요.'; return }
      this.memberId = id
      this.lookupVersion++
    },
    openMember (id) { this.memberSearch = id; this.findMember(); this.mode = 'member' },
    openList () { this.mode = 'list'; this.searchList() },
    searchList () {
      if (this.loading) return
      this.query = {}
      if (this.filters.category) this.query.category = this.filters.category
      if (this.filters.memId.trim()) this.query.memId = this.filters.memId.trim()
      return this.loadList(1)
    },
    async loadList (page) {
      if (this.loading || !Number.isInteger(page) || page < 1) return
      this.loading = true
      this.listError = ''
      this.members = []
      try {
        const response = await getMemberBetPointList({ ...this.query, page, count_per_list: 20 })
        if (response?.resultCode !== '0') throw new Error(({ '9002': '검색 조건을 확인해주세요.', '9004': '사이트 또는 회원을 찾을 수 없습니다.', '1000': '조회 권한이 없습니다.', TK999: '로그인 정보가 변경되었습니다. 다시 로그인해주세요.', '9999': '인증 또는 서버 오류가 발생했습니다.' })[response?.resultCode] || response?.resultMessage || '목록을 조회하지 못했습니다.')
        const list = response.data?.list
        if (!Array.isArray(list) || list.some(row => typeof row?.memId !== 'string' || !row.memId.trim())) throw new Error('회원 목록 응답 형식을 확인하지 못했습니다.')
        this.members = list
        this.page = page
        const info = response.data?.pageInfo
        this.totalCount = Number.isInteger(info?.totalCount) ? info.totalCount : null
        this.hasNext = this.totalCount === null ? list.length === 20 : page * 20 < this.totalCount
      } catch (error) {
        this.listError = [401, 422].includes(error.response?.status) || error.message === 'No token' ? '로그인이 만료되었습니다. 다시 로그인해주세요.' : error.response || error.code ? '서버에 연결하지 못했습니다. 잠시 후 다시 시도해주세요.' : error.message || '목록을 조회하지 못했습니다.'
      } finally { this.loading = false }
    }
  }
}
</script>
<style scoped>
.page-title {font-size:20px;margin:12px 0 20px;}.intro {color:#cbd5e1;line-height:1.6;margin:12px 0;}.list {margin:18px 0;padding:18px;background:#1f2937;border:1px solid #313a49;border-radius:5px;color:#d1d5db;}.list h3 {font-size:16px;}
.search,.pages {display:flex;flex-wrap:wrap;align-items:center;gap:12px;margin:18px 0;}.search label {display:flex;align-items:center;gap:8px;}.search input,.search select {padding:8px;border:1px solid #64748b;background:#111827;color:#fff;}
button:not(.tab-item) {padding:8px 12px;background:#374151;color:#fff;border-radius:4px;cursor:pointer;font:inherit;}button:disabled {opacity:.5;cursor:default;}button:focus-visible,input:focus-visible,select:focus-visible {outline:2px solid #93c5fd;outline-offset:2px;}.error {color:#fca5a5;}.table-scroll {overflow-x:auto;}.mainTable {min-width:320px;}
</style>
